import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Handshake, ShieldCheck, GraduationCap, Cpu, Megaphone, LifeBuoy, Users, ArrowRight } from 'lucide-react';
import { fadeUp, staggerParent, viewportOnce } from './motionPresets';

const PILLARS = [
  { icon: Handshake, title: 'Contracting Support', desc: 'Guided carrier contracting with clear statuses, follow-ups, and reminders.', span: 'lg:col-span-2', accent: true },
  { icon: ShieldCheck, title: 'Carrier Access', desc: 'Top Health & Life carriers matched to your market, license, and product focus.' },
  { icon: GraduationCap, title: 'Training & Development', desc: 'Live and on-demand education, product deep-dives, and sales coaching.' },
  { icon: Cpu, title: 'Technology', desc: 'The proprietary KR Agency Hub — CRM, messaging, and compliance in one place.', span: 'lg:col-span-2', accent: true },
  { icon: Megaphone, title: 'Marketing Support', desc: 'Turnkey templates, retail programs, and localized, compliant collateral.' },
  { icon: LifeBuoy, title: 'Operational Support', desc: 'Real people handling paperwork, appointments, and follow-through.' },
  { icon: Users, title: 'Community & Events', desc: 'Regional summits, roundtables, and a peer network that grows with you.' },
];

export default function WhyKR() {
  return (
    <section id="why-kr" className="scroll-mt-28 lg:scroll-mt-32 bg-white py-20 sm:py-28 lg:py-32" data-testid="why-kr-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div variants={staggerParent} initial="hidden" whileInView="show" viewport={viewportOnce} className="max-w-3xl">
          <motion.span variants={fadeUp} className="text-xs font-semibold uppercase tracking-widest text-[#b8862d]">A partner in your success</motion.span>
          <motion.h2 variants={fadeUp} className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">Why agents build with KR.</motion.h2>
          <motion.p variants={fadeUp} className="mt-5 text-lg text-slate-600 leading-relaxed">
            We give independent agents and agencies the support, resources, and infrastructure to grow a stronger, more
            successful business — an interconnected ecosystem, not a checklist of services.
          </motion.p>
        </motion.div>

        <motion.div
          className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
          variants={staggerParent}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
        >
          {PILLARS.map((p) => (
            <motion.div
              key={p.title}
              variants={fadeUp}
              className={`group relative rounded-2xl border p-6 sm:p-7 transition-all duration-300 hover:-translate-y-1.5 ${
                p.accent
                  ? 'bg-[#0f2a5e] border-[#0f2a5e] text-white shadow-lg hover:shadow-2xl ' + (p.span || '')
                  : 'bg-white border-slate-200 hover:border-[#b8862d]/50 hover:shadow-xl ' + (p.span || '')
              }`}
            >
              <span className={`inline-flex w-12 h-12 rounded-xl items-center justify-center ${p.accent ? 'bg-white/10 text-[#f5c876]' : 'bg-[#0f2a5e]/5 text-[#0f2a5e] group-hover:bg-[#b8862d]/10 group-hover:text-[#b8862d]'} transition-colors`}>
                <p.icon size={22} />
              </span>
              <h3 className={`mt-5 text-lg font-bold ${p.accent ? 'text-white' : 'text-slate-900'}`}>{p.title}</h3>
              <p className={`mt-2 text-sm leading-relaxed ${p.accent ? 'text-slate-300' : 'text-slate-600'}`}>{p.desc}</p>
            </motion.div>
          ))}

          <motion.div variants={fadeUp} className="rounded-2xl border border-dashed border-[#b8862d]/40 bg-[#fdf8ec] p-6 sm:p-7 flex flex-col justify-center">
            <h3 className="text-lg font-bold text-slate-900">Ready to see it in action?</h3>
            <p className="mt-2 text-sm text-slate-600">Start a conversation with the KR team.</p>
            <Link to="/become-an-agent" data-testid="whykr-cta" className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#b8862d] hover:gap-3 transition-all">
              Learn more about KR <ArrowRight size={15} />
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
